import { Outlet, Route } from "react-location";
import { AccessRequestsPage, DashboardPage, ForgotPasswordPage, SigninPage, UsersPage, RolesPage, GroupsPage, ChangePasswordPage, ReviewsPage, TwoFactorAuthPage } from "pages";
import { ChartBarSquareIcon, ArrowRightOnRectangleIcon, ClipboardDocumentListIcon, ShieldCheckIcon, UserGroupIcon, UserIcon, StarIcon } from "@heroicons/react/24/outline";
import { UserPermission, initiateClearAuth } from "state/auth";

export type RouteProps = Omit<Route, "children"> & {
  withPermissions?: UserPermission[];
  navigation?: boolean;
  sidebar?: {
    level: "primary" | "secondary";
    category?: "menu" | "app" | "settings";
    label: string;
    icon?: any;
    onClick?: () => void;
  };
  children?: RouteProps[];
};

const routes: RouteProps[] = [
  {
    path: "/",
    sidebar: {
      level: "primary",
      category: "menu",
      label: "Dashboard",
      icon: ChartBarSquareIcon,
    },
    meta: {
      layout: "App",
      breadcrumb: () => "Dashboard",
      section: "General",
    },
    children: [
      {
        id: "all",
        path: "/",
        element: <DashboardPage />,
        meta: {
          layout: "App",
          section: "General",
        },
      },
    ],
  },
  {
    path: "access-requests",
    element: <Outlet />,
    sidebar: {
      level: "primary",
      category: "app",
      label: "Access Requests",
      icon: ClipboardDocumentListIcon,
    },
    meta: {
      layout: "App",
      breadcrumb: () => "Access Requests",
      section: "General",
    },
    children: [
      {
        id: "all",
        path: "/",
        element: <AccessRequestsPage />,
        meta: {
          layout: "App",
          section: "General",
        },
      },
    ],
  },
  {
    path: "users",
    element: <Outlet />,
    sidebar: {
      level: "primary",
      category: "settings",
      label: "Users",
      icon: UserIcon,
    },
    withPermissions: ["user:view"],
    meta: {
      layout: "App",
      breadcrumb: () => "Users",
      section: "Administration",
    },
    children: [
      {
        id: "all",
        path: "/",
        element: <UsersPage />,
        meta: {
          layout: "App",
          section: "Administration",
        },
      },
    ],
  },
  {
    path: "roles",
    element: <Outlet />,
    sidebar: {
      level: "primary",
      category: "settings",
      label: "Roles",
      icon: ShieldCheckIcon,
    },
    withPermissions: ["role:view"],
    meta: {
      layout: "App",
      breadcrumb: () => "Roles",
      section: "Administration",
    },
    children: [
      {
        id: "all",
        path: "/",
        element: <RolesPage />,
        meta: {
          layout: "App",
          section: "Administration",
        },
      },
    ],
  },
  {
    path: "groups",
    element: <Outlet />,
    sidebar: {
      level: "primary",
      category: "settings",
      label: "Groups",
      icon: UserGroupIcon,
    },
    withPermissions: ["group:view"],
    meta: {
      layout: "App",
      breadcrumb: () => "Groups",
      section: "Administration",
    },
    children: [
      {
        id: "all",
        path: "/",
        element: <GroupsPage />,
        meta: {
          layout: "App",
          section: "Administration",
        },
      },
    ],
  },
  {
    path: "reviews",
    element: <Outlet />,
    sidebar: {
      level: "primary",
      category: "settings",
      label: "Reviews",
      icon: StarIcon,
    },
    withPermissions: ["*"],
    meta: {
      layout: "App",
      breadcrumb: () => "Service Reviews",
      section: "Administration",
    },
    children: [
      {
        id: "all",
        path: "/",
        element: <ReviewsPage />,
        meta: {
          layout: "App",
          section: "Administration",
        },
      },
    ],
  },
  {
    path: "security",
    element: <TwoFactorAuthPage />,
    meta: {
      layout: "Auth",
      breadcrumb: () => "Security",
    },
  },
  {
    path: "signout",
    element: <Outlet />,
    sidebar: {
      level: "secondary",
      label: "Logout",
      icon: ArrowRightOnRectangleIcon,
      onClick: initiateClearAuth,
    },
    meta: {
      layout: "App",
      breadcrumb: () => "Logout",
      section: "Configuration",
    },
  },
  {
    path: "forgot-password",
    element: <ForgotPasswordPage />,
    meta: {
      layout: "Auth",
      breadcrumb: () => "Forgot Password",
    },
  },
  {
    path: "signin",
    element: <SigninPage />,
    meta: {
      layout: "Auth",
      breadcrumb: () => "Signin",
    },
  },
  {
    path: "change-password",
    element: <ChangePasswordPage />,
    meta: {
      layout: "Auth",
      breadcrumb: () => "Change Password",
    },
  },
];

export default routes;
